import axios from 'axios';
import API_URL from '../config/API_URL';

const API_BASE_URL = `${API_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Store error handler reference
let errorHandler = null;

export const setErrorHandler = (handler) => {
  errorHandler = handler;
};

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Check if this is NOT the getCurrentUser endpoint
    const isAuthMeEndpoint = error.config?.url?.includes('/auth/me');
    
    const status = error.response?.status;
    
    // Network errors (like ERR_CONNECTION_REFUSED) don't have a response object
    const isNetworkError = !error.response;
    
    // Server errors (5xx)
    const isServerError = status >= 500;
    
    // Client errors that indicate a problem (but not auth issues)
    const isClientErrorThatMatters = status === 404 || status === 400;
    
    const shouldShowError = !isAuthMeEndpoint && (isNetworkError || isServerError || isClientErrorThatMatters);
    
    if (shouldShowError && errorHandler) {
      errorHandler();
    }
    
    return Promise.reject(error);
  }
);
// Auth Services
export const authService = {
  register: async (name, email, password) => {
    const response = await api.post('/auth/register', { name, email, password });
    return response.data;
  },

  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
  forgotPassword: async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  verifyResetToken: async (token) => {
    const response = await api.post('/auth/verify-reset-token', { token });
    return response.data;
  },

  resetPassword: async (token, password) => {
    const response = await api.post('/auth/reset-password', { token, password });
    return response.data;
  },
};

// Rest of your services remain the same...
export const contactService = {
  getContacts: async () => {
    const response = await api.get('/contacts');
    return response.data;
  },

  getContact: async (id) => {
    const response = await api.get(`/contacts/${id}`);
    return response.data;
  },

  createContact: async (contactData) => {
    const response = await api.post('/contacts', contactData);
    return response.data;
  },

  updateContact: async (id, contactData) => {
    const response = await api.put(`/contacts/${id}`, contactData);
    return response.data;
  },

  deleteContact: async (id) => {
    const response = await api.delete(`/contacts/${id}`);
    return response.data;
  },

  addInteraction: async (contactId, interactionData) => {
    const response = await api.post(`/contacts/${contactId}/interactions`, interactionData);
    return response.data;
  },

  addNote: async (contactId, content) => {
    const response = await api.post(`/contacts/${contactId}/notes`, { content });
    return response.data;
  },

  updateNote: async (contactId, noteId, content) => {
    const response = await api.put(`/contacts/${contactId}/notes/${noteId}`, { content });
    return response.data;
  },

  deleteNote: async (contactId, noteId) => {
    const response = await api.delete(`/contacts/${contactId}/notes/${noteId}`);
    return response.data;
  },
  addImportantDate: async (contactId, dateData) => {
    const response = await api.post(`/contacts/${contactId}/dates`, dateData);
    return response.data;
  },
  
  getImportantDates: async (contactId) => {
    const response = await api.get(`/contacts/${contactId}/dates`);
    return response.data;
  },
  
  updateImportantDate: async (contactId, dateId, dateData) => {
    const response = await api.put(`/contacts/${contactId}/dates/${dateId}`, dateData);
    return response.data;
  },
  
  deleteImportantDate: async (contactId, dateId) => {
    const response = await api.delete(`/contacts/${contactId}/dates/${dateId}`);
    return response.data;
  }
};
export const schedulerService = {
  getSchedulers: async () => {
    const response = await api.get('/scheduler/schedulers');
    return response.data;
  },

  getFindings: async () => {
    const response = await api.get('/scheduler/findings');
    return response.data;
  },

  createScheduler: async (contactId, frequency, attributes) => {
    const response = await api.post('/scheduler/schedulers', {
      contact_id: contactId,
      frequency: frequency,
      monitor_attributes: attributes,
    });
    return response.data;
  },

  deleteScheduler: async (schedulerId) => {
    const response = await api.delete(`/scheduler/schedulers/${schedulerId}`);
    return response.data;
  },

  checkNow: async (schedulerId) => {
    const response = await api.post(`/scheduler/check-now/${schedulerId}`);
    return response.data;
  },

  markFindingRead: async (findingId) => {
    const response = await api.post(`/scheduler/findings/${findingId}/mark-read`);
    return response.data;
  },
};

export const aiService = {
  generateMessage: async (contactId, messageType, medium, context, template = '') => {
    const response = await fetch(`${API_URL}/api/generate-message`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        contact_id: contactId,
        message_type: messageType,
        medium: medium,
        context: context,
        template: template
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to generate message');
    }

    return response.json();
  },

  transcribeAndExtractInteraction: async (audioBlob) => {
    const formData = new FormData();
    formData.append('audio', audioBlob);
    
    const response = await api.post('/transcribe-and-extract-interaction', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    });
    return response.data;
  },

  refineMessage: async (contactId, messageType, medium, context, conversationHistory, template = '') => {
    const response = await fetch(`${API_URL}/api/refine-message`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        contact_id: contactId,
        message_type: messageType,
        medium: medium,
        context: context,
        conversation_history: conversationHistory,
        template: template
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to refine message');
    }

    return response.json();
  },

  processVoiceMemo: async (transcript) => {
    const response = await api.post('/process-voice-memo', { transcript });
    return response.data;
  },

  transcribeAndExtract: async (audioBlob) => {
    const formData = new FormData();
    formData.append('audio', audioBlob);
    
    const response = await api.post('/transcribe-and-extract', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    });
    return response.data;
  },

  generateMessageFromFinding: async (findingId, messageType, medium, additionalContext = '') => {
    const response = await api.post('/generate-message-from-finding', {
      finding_id: findingId,
      message_type: messageType,
      medium: medium,
      additional_context: additionalContext,
    });
    return response.data;
  },
};

export const profileService = {
  getProfile: async () => {
    const response = await api.get('/profile/');
    return response.data;
  },

  updateProfile: async (profileData) => {
    const response = await api.post('/profile/', profileData);
    return response.data;
  },
};

export default api;